import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../api/client';
import './AmazonSkuMappingPopup.css';

const EDITABLE_FIELDS = ['merchant_sku', 'asin', 'box_length_in', 'box_width_in', 'box_height_in', 'box_weight_lb'];
const EMPTY_DRAFT     = { merchant_sku: '', asin: '', box_length_in: '', box_width_in: '', box_height_in: '', box_weight_lb: '' };

const AmazonSkuMappingPopup = ({ show, onClose }) => {
  const [rows, setRows]                   = useState([]);
  const [bagsMap, setBagsMap]             = useState({});   // { SKU: { bagsPerBox, source_table } }
  const [availableProducts, setAvailable] = useState([]);   // products not yet in mapping
  const [search, setSearch]               = useState('');
  const [loading, setLoading]             = useState(false);

  // Edit existing
  const [editingId, setEditingId]   = useState(null);
  const [editDraft, setEditDraft]   = useState({});
  const [editBags, setEditBags]     = useState('');   // inline bags/box edit
  const [saving, setSaving]         = useState(false);
  const [saveError, setSaveError]   = useState('');

  // Add new
  const [addingNew, setAddingNew]         = useState(false);
  const [selectedProduct, setSelected]    = useState(null);  // product chosen from dropdown
  const [productSearch, setProductSearch] = useState('');
  const [dropdownOpen, setDropdownOpen]   = useState(false);
  const [newDraft, setNewDraft]           = useState({ ...EMPTY_DRAFT });

  const searchRef   = useRef(null);
  const dropdownRef = useRef(null);

  // ── Load data ────────────────────────────────────────────────────────────
  const loadMapping = useCallback(async () => {
    try {
      const { data: d } = await api.get('/api/amazon_sku_mapping?limit=500&sortKey=product_name&sortOrder=ASC');
      setRows(d.items || []);
    } catch (e) { if (!e.response) throw e; }
  }, []);

  const loadBags = useCallback(async () => {
    try {
      const { data: list } = await api.get('/shipments-planning/sku-mapping-bags-per-box');
      const m = {};
      list.forEach(r => { m[r.SKU] = { bagsPerBox: r.bagsPerBox, source_table: r.source_table }; });
      setBagsMap(m);
    } catch (e) { if (!e.response) throw e; }
  }, []);

  const loadAvailable = useCallback(async () => {
    try {
      const { data } = await api.get('/shipments-planning/sku-mapping-available-products');
      setAvailable(data);
    } catch (e) { if (!e.response) throw e; }
  }, []);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try { await Promise.all([loadMapping(), loadBags(), loadAvailable()]); }
    finally { setLoading(false); }
  }, [loadMapping, loadBags, loadAvailable]);

  useEffect(() => {
    if (show) {
      loadAll();
      setEditingId(null); setAddingNew(false); setSelected(null);
      setProductSearch(''); setSaveError('');
      setTimeout(() => searchRef.current?.focus(), 50);
    }
  }, [show, loadAll]);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = e => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setDropdownOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // ── Filtered rows for main table ─────────────────────────────────────────
  const filtered = rows.filter(r => {
    const q = search.toLowerCase();
    return !q || (r.product_name||'').toLowerCase().includes(q)
      || (r.sku_organo||'').toLowerCase().includes(q)
      || (r.merchant_sku||'').toLowerCase().includes(q)
      || (r.asin||'').toLowerCase().includes(q);
  });

  // ── Filtered available products for dropdown ─────────────────────────────
  const filteredAvailable = availableProducts.filter(p => {
    const q = productSearch.toLowerCase();
    return !q || (p.name||'').toLowerCase().includes(q) || (p.SKU||'').toLowerCase().includes(q);
  });

  // ── Editing existing ─────────────────────────────────────────────────────
  const startEdit = row => {
    setEditingId(row.id);
    setEditDraft(Object.fromEntries(EDITABLE_FIELDS.map(f => [f, row[f] ?? ''])));
    setEditBags(bagsMap[row.sku_organo]?.bagsPerBox ?? '');
    setSaveError('');
    setAddingNew(false);
  };

  const cancelEdit = () => { setEditingId(null); setEditDraft({}); setSaveError(''); };

  const saveEdit = async (row) => {
    setSaving(true); setSaveError('');
    try {
      await api.put(`/api/amazon_sku_mapping/${row.id}`, editDraft)
        .catch(err => { throw err.response ? new Error('Failed to update') : err; });

      // Update bagsPerBox if changed
      const srcTable = row.source_table || bagsMap[row.sku_organo]?.source_table;
      const origBags = bagsMap[row.sku_organo]?.bagsPerBox;
      if (srcTable && String(editBags) !== String(origBags) && editBags !== '') {
        await api.put('/shipments-planning/sku-mapping-bags-per-box',
          { sku: row.sku_organo, source_table: srcTable, value: Number(editBags) })
          .catch(err => { if (!err.response) throw err; });
      }

      setEditingId(null);
      await Promise.all([loadMapping(), loadBags()]);
    } catch (e) { setSaveError(e.message); }
    finally { setSaving(false); }
  };

  // ── Adding new ───────────────────────────────────────────────────────────
  const startNew = () => {
    setAddingNew(true); setEditingId(null);
    setSelected(null); setProductSearch(''); setNewDraft({ ...EMPTY_DRAFT }); setSaveError('');
  };

  const cancelNew = () => { setAddingNew(false); setSelected(null); setProductSearch(''); setSaveError(''); };

  const selectProduct = (p) => {
    setSelected(p);
    setNewDraft(prev => ({
      ...prev,
      asin: p.ASIN || '',
    }));
    setProductSearch(p.name || p.SKU);
    setDropdownOpen(false);
  };

  const saveNew = async () => {
    if (!selectedProduct) { setSaveError('Please select a product from the list.'); return; }
    setSaving(true); setSaveError('');
    try {
      const payload = {
        sku_organo:    selectedProduct.SKU,
        source_table:  selectedProduct.source_table,
        product_name:  selectedProduct.name,
        ...newDraft,
      };
      await api.post('/api/amazon_sku_mapping', payload)
        .catch(err => { throw err.response ? new Error('Failed to create') : err; });
      setAddingNew(false); setSelected(null);
      await Promise.all([loadMapping(), loadBags(), loadAvailable()]);
    } catch (e) { setSaveError(e.message); }
    finally { setSaving(false); }
  };

  // ── Helpers ──────────────────────────────────────────────────────────────
  const numIn = (val, onChange, placeholder) => (
    <input className="skum-input skum-input--dim" type="number" placeholder={placeholder}
      value={val} onChange={e => onChange(e.target.value)} />
  );

  const actionBtns = (onSave) => (
    <td className="skum-td skum-td--actions">
      <button className="skum-btn skum-btn--save" onClick={onSave} disabled={saving}>
        {saving ? <i className="fas fa-circle-notch fa-spin"></i> : <i className="fas fa-check"></i>}
      </button>
      <button className="skum-btn skum-btn--cancel" onClick={editingId ? cancelEdit : cancelNew}>
        <i className="fas fa-times"></i>
      </button>
    </td>
  );

  if (!show) return null;

  return (
    <div className="popup-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="popup-content skum-popup">

        {/* Header */}
        <div className="skum-header">
          <div className="skum-header-left">
            <i className="fas fa-boxes skum-header-icon"></i>
            <div>
              <h3 className="skum-title">SKU Mapping &amp; Box Settings</h3>
              <p className="skum-subtitle">FNSKU, ASIN, box dimensions and bags/box for Amazon uploads</p>
            </div>
          </div>
          <button className="skum-close" onClick={onClose}><i className="fas fa-times"></i></button>
        </div>

        {/* Toolbar */}
        <div className="skum-toolbar">
          <div className="skum-search-wrap">
            <i className="fas fa-search skum-search-icon"></i>
            <input ref={searchRef} className="skum-search"
              placeholder="Search by name, SKU, Merchant SKU or ASIN…"
              value={search} onChange={e => setSearch(e.target.value)} />
            {search && <button className="skum-search-clear" onClick={() => setSearch('')}><i className="fas fa-times"></i></button>}
          </div>
          <button className="skum-btn skum-btn--add" onClick={startNew} disabled={addingNew || editingId !== null}>
            <i className="fas fa-plus"></i> Add SKU
          </button>
        </div>

        {saveError && <div className="skum-error">{saveError}</div>}

        {/* Add new row */}
        {addingNew && (
          <div className="skum-new-panel">
            <div className="skum-new-row">
              {/* Searchable product dropdown */}
              <div className="skum-new-field skum-new-field--product" ref={dropdownRef}>
                <label className="skum-new-label">Product</label>
                <div className="skum-dropdown-wrap">
                  <div className="skum-dropdown-input-wrap">
                    <i className="fas fa-search skum-search-icon"></i>
                    <input
                      className="skum-search skum-dropdown-input"
                      placeholder="Search product name or SKU…"
                      value={productSearch}
                      onChange={e => { setProductSearch(e.target.value); setSelected(null); setDropdownOpen(true); }}
                      onFocus={() => setDropdownOpen(true)}
                    />
                  </div>
                  {dropdownOpen && filteredAvailable.length > 0 && (
                    <div className="skum-dropdown-list">
                      {filteredAvailable.map(p => (
                        <div key={p.SKU} className="skum-dropdown-item" onClick={() => selectProduct(p)}>
                          <span className="skum-dropdown-name">{p.name}</span>
                          <span className="skum-dropdown-sku">{p.SKU}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {dropdownOpen && filteredAvailable.length === 0 && productSearch && (
                    <div className="skum-dropdown-list">
                      <div className="skum-dropdown-empty">No products found</div>
                    </div>
                  )}
                </div>
                {selectedProduct && (
                  <div className="skum-selected-info">
                    <span className="skum-selected-sku">{selectedProduct.SKU}</span>
                    {selectedProduct.ASIN && <span className="skum-selected-asin">ASIN: {selectedProduct.ASIN}</span>}
                  </div>
                )}
              </div>

              {/* Editable fields */}
              <div className="skum-new-field">
                <label className="skum-new-label">FNSKU / Merchant SKU</label>
                <input className="skum-input" placeholder="BA-8CME-RC9P"
                  value={newDraft.merchant_sku} onChange={e => setNewDraft(p => ({ ...p, merchant_sku: e.target.value }))} />
              </div>
              <div className="skum-new-field skum-new-field--dims">
                <label className="skum-new-label">L × W × H (in)</label>
                <div className="skum-dims-row">
                  {numIn(newDraft.box_length_in, v => setNewDraft(p => ({ ...p, box_length_in: v })), 'L')}
                  <span className="skum-x">×</span>
                  {numIn(newDraft.box_width_in,  v => setNewDraft(p => ({ ...p, box_width_in:  v })), 'W')}
                  <span className="skum-x">×</span>
                  {numIn(newDraft.box_height_in, v => setNewDraft(p => ({ ...p, box_height_in: v })), 'H')}
                </div>
              </div>
              <div className="skum-new-field skum-new-field--sm">
                <label className="skum-new-label">Weight (lb)</label>
                {numIn(newDraft.box_weight_lb, v => setNewDraft(p => ({ ...p, box_weight_lb: v })), 'lb')}
              </div>
              <div className="skum-new-actions">
                <button className="skum-btn skum-btn--save" onClick={saveNew} disabled={saving || !selectedProduct}>
                  {saving ? <i className="fas fa-circle-notch fa-spin"></i> : <><i className="fas fa-check"></i> Save</>}
                </button>
                <button className="skum-btn skum-btn--cancel" onClick={cancelNew}><i className="fas fa-times"></i> Cancel</button>
              </div>
            </div>
          </div>
        )}

        {/* Table */}
        <div className="skum-table-wrap">
          {loading ? (
            <div className="skum-loading"><i className="fas fa-circle-notch fa-spin"></i> Loading…</div>
          ) : (
            <table className="skum-table">
              <thead>
                <tr>
                  <th className="skum-th skum-th--name">Product Name</th>
                  <th className="skum-th">SKU Organo</th>
                  <th className="skum-th">FNSKU / Merchant SKU</th>
                  <th className="skum-th">ASIN</th>
                  <th className="skum-th">L × W × H (in)</th>
                  <th className="skum-th skum-th--num">Weight (lb)</th>
                  <th className="skum-th skum-th--num">Bags/box</th>
                  <th className="skum-th skum-th--actions"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr><td colSpan={8} className="skum-empty">
                    {search ? 'No results for your search.' : 'No SKUs found.'}
                  </td></tr>
                ) : filtered.map(row => {
                  const bags = bagsMap[row.sku_organo];
                  const isEditing = editingId === row.id;
                  return isEditing ? (
                    <tr key={row.id} className="skum-row skum-row--editing">
                      <td className="skum-td"><span className="skum-ro-name">{row.product_name || <span className="skum-muted">—</span>}</span></td>
                      <td className="skum-td"><span className="skum-ro-sku">{row.sku_organo}</span></td>
                      <td className="skum-td">
                        <input className="skum-input" placeholder="FNSKU / Merchant SKU"
                          value={editDraft.merchant_sku} onChange={e => setEditDraft(p => ({ ...p, merchant_sku: e.target.value }))} />
                      </td>
                      <td className="skum-td">
                        <input className="skum-input" placeholder="ASIN"
                          value={editDraft.asin} onChange={e => setEditDraft(p => ({ ...p, asin: e.target.value }))} />
                      </td>
                      <td className="skum-td skum-td--dims">
                        {numIn(editDraft.box_length_in, v => setEditDraft(p => ({ ...p, box_length_in: v })), 'L')}
                        <span className="skum-x">×</span>
                        {numIn(editDraft.box_width_in,  v => setEditDraft(p => ({ ...p, box_width_in:  v })), 'W')}
                        <span className="skum-x">×</span>
                        {numIn(editDraft.box_height_in, v => setEditDraft(p => ({ ...p, box_height_in: v })), 'H')}
                        <span className="skum-x-unit">in</span>
                      </td>
                      <td className="skum-td">
                        {numIn(editDraft.box_weight_lb, v => setEditDraft(p => ({ ...p, box_weight_lb: v })), 'lb')}
                      </td>
                      <td className="skum-td">
                        {bags?.source_table
                          ? numIn(editBags, v => setEditBags(v), '—')
                          : <span className="skum-muted">—</span>}
                      </td>
                      {actionBtns(() => saveEdit(row))}
                    </tr>
                  ) : (
                    <tr key={row.id} className="skum-row" onDoubleClick={() => !editingId && !addingNew && startEdit(row)}>
                      <td className="skum-td skum-td--name">{row.product_name || <span className="skum-muted">—</span>}</td>
                      <td className="skum-td skum-td--mono">{row.sku_organo}</td>
                      <td className="skum-td skum-td--mono">{row.merchant_sku || <span className="skum-muted">—</span>}</td>
                      <td className="skum-td skum-td--mono skum-td--asin">{row.asin || <span className="skum-muted">—</span>}</td>
                      <td className="skum-td skum-td--dims-ro">
                        {row.box_length_in && row.box_width_in && row.box_height_in
                          ? `${row.box_length_in} × ${row.box_width_in} × ${row.box_height_in}`
                          : <span className="skum-muted">—</span>}
                      </td>
                      <td className="skum-td skum-td--num">{row.box_weight_lb != null ? row.box_weight_lb : <span className="skum-muted">—</span>}</td>
                      <td className="skum-td skum-td--num">{bags?.bagsPerBox != null ? bags.bagsPerBox : <span className="skum-muted">—</span>}</td>
                      <td className="skum-td skum-td--actions">
                        <button className="skum-btn skum-btn--edit" onClick={() => startEdit(row)}
                          disabled={editingId !== null || addingNew} title="Edit">
                          <i className="fas fa-pen"></i>
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Footer */}
        <div className="skum-footer">
          <span className="skum-count">{filtered.length} of {rows.length} SKUs</span>
          <button className="skum-btn skum-btn--close-footer" onClick={onClose}>Close</button>
        </div>

      </div>
    </div>
  );
};

export default AmazonSkuMappingPopup;
